import { Checkmark, CheckmarkVariant } from '@/components/Checkmark';
import classnames from '@/utilities/classnames';
import isEqual from '@/utilities/isEqual';
import replaceAbbreviations from '@/utilities/replaceAbbreviations';

export enum RadioGroupVariant {
  Standard = 'standard',
  Stacked = 'stacked',
  Full = 'full',
}

export interface RadioProps {
  value: readonly string[];
  label: {
    text: readonly string[];
  };
}

export const RadioGroup = ({
  variant = RadioGroupVariant.Standard,
  shortName,
  radios,
  id,
  title,
  onChange,
  currentChecked,
}: {
  id: string;
  title: string;
  onChange: (value: readonly string[]) => void;
  currentChecked?: string[];
  radios: readonly RadioProps[];
  variant?: RadioGroupVariant;
  shortName?: boolean;
}) => {
  const renderShortName = (strings) => {
    return (
      shortName ? strings.map((str) => str.substr(0, 2) + '.') : strings
    ).join(' + ');
  };
  return (
    <div
      role="radiogroup"
      aria-label={title}
      className={classnames('RadioGroup', {
        [`RadioGroup--${variant}`]: !!variant,
      })}
    >
      {radios.map((radio: RadioProps, index: number) => {
        const inputId: string = `${id}-${index + 1}`;
        const isChecked: boolean =
          typeof currentChecked !== 'undefined' &&
          isEqual(radio.value, currentChecked);
        const checked = currentChecked && isChecked;
        return (
          <div
            key={index}
            className={classnames(
              'RadioGroup__item',
              `RadioGroup__item--${variant}`,
              {
                [`RadioGroup__item--checked`]: isChecked,
              }
            )}
          >
            <input
              id={inputId}
              value={radio.value}
              name={inputId}
              className="RadioGroup__input"
              type="radio"
              onChange={() => onChange(radio.value)}
              checked={checked}
            />

            <label
              htmlFor={inputId}
              className={classnames(
                'RadioGroup__label',
                `RadioGroup__label--${variant}`
              )}
              tabIndex={0}
              aria-label={`An Ziehung von ${radio.label.text.join(
                ' und '
              )} teilnehmen`}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  onChange(radio.value);
                }
              }}
            >
              {variant !== RadioGroupVariant.Stacked && (
                <Checkmark
                  checked={isChecked}
                  variant={CheckmarkVariant.Round}
                />
              )}
              <span
                dangerouslySetInnerHTML={replaceAbbreviations(
                  renderShortName(radio.label.text)
                )}
              />
            </label>
          </div>
        );
      })}
    </div>
  );
};
